import React, { useRef, useEffect, useState } from 'react';
import ReactSlider from 'react-slick';

import createStyle from 'shared-utils/src/createStyle';
import makeClassName from 'shared-utils/src/makeClassName';
import trackGTMEvents from 'shared-utils/src/trackGTMEvents/new';
import sleep from 'shared-utils/src/sleep';

import Arrow from 'shared-vectors/icons/GalleryArrowSml';
import FloorPlan from 'shared-vectors/icons/Floorplan';
import GoFullScreen from 'shared-vectors/icons/GoFullScreen';
import Video from 'shared-vectors/icons/Video';
import VirtualTour from 'shared-vectors/icons/VirtualTour';
import Matterpot from 'shared-vectors/icons/MatterportNew';
import { Icon } from 'shared-vectors';

import Grid from '../../../Grid';
import GridItem from '../../../GridItem';

import Tooltip from '../Tooltip';

import CtaSlide from './CtaSlide';
import Image from './Image';

import usePreventVerticalScroll from '../../utils/handleVerticalScroll';
import { computeToLoad, getPrice } from '../../utils';

import styles, { mainCls } from './style';

const useStyles = createStyle(styles);

export default function Gallery({
  size,
  media,
  labels,
  id,
  visible,
  forceVisible,
  actions,
  uri,
  topRound,
  botRound,
  setCurrentFloorplan,
  sendMail,
  title,
  isHoliday,
  showMqPrice,
  isProjectProfile,
  projectData,
  showMortgage,
  isAuction,
  features,
  maplist,
  mapcard,
  isMobile,
}) {
  const cls = useStyles();

  const { mq, price } = features;

  const container = useRef(null);
  const slider = useRef(null);

  usePreventVerticalScroll(container);

  const [current, setCurrent] = useState(0);
  const [toLoad, setToLoad] = useState([current]);
  const floorplan = media.floorplan || 0;
  // ?? CHECK
  const floorplansMedia =
    floorplan &&
    media &&
    media?.items &&
    (media?.items?.slice(floorplan) || []);
  // ?? CHECK
  const [currentIsFloorplan, setCurrentIsFloorplan] = useState(false);
  const [isFloorplan, setIsFloorplan] = useState(
    media.items[current]?.type === 'fp',
  );
  const [isFloorplanClosing, setIsFloorplanClosing] = useState(
    media.items[current]?.type === 'fp',
  );

  useEffect(() => {
    const delta = floorplan + (floorplansMedia.length || 1);
    if (current >= floorplan && current <= delta) {
      setIsFloorplan(true);
    } else if (current < floorplan || current > delta) {
      setIsFloorplanClosing(true);
      setIsFloorplan(false);
      setTimeout(() => setIsFloorplanClosing(false), 250);
    }
  }, [current, media]);

  const updateCurrent = (oldIndex, newIndex) => {
    setCurrent(newIndex);
    actions.setImg(newIndex);
    if (toLoad.length < media.items.length) {
      setToLoad(computeToLoad(newIndex, toLoad));
    }
    if (floorplan) {
      if (newIndex >= floorplan) {
        setCurrentIsFloorplan(true);
      } else {
        setCurrentIsFloorplan(false);
      }
    }
  };

  const priceEl = getPrice({
    className: `${cls[`${mainCls}--price`]} tp-w--l`,
    price,
    projectData: isProjectProfile ? projectData : null,
    isAuction,
    showMortgage,
    isHoliday,
    showMqPrice,
    mq,
  });

  const prevSlide = () => slider.current.slickPrev();
  const nextSlide = () => slider.current.slickNext();
  const gotoFloorplan = () => {
    let gtmPosition = 'SRP_CardSlider';
    if (mapcard) {
      gtmPosition = 'SRPMap_CardMapArea';
    } else if (maplist) {
      gtmPosition = 'SRPMap_CardList';
    }
    
    if (currentIsFloorplan) {
      setCurrentFloorplan({
        showFloorplan: true,
        floorplans: floorplansMedia,
        current: current - floorplan,
        uri,
        sendMail,
        title,
        priceEl,
      });
      // setShowFloorplan(true);
      sleep.apply(300).then(() => trackGTMEvents({
      category: 'Interaction',
      action: 'OpenGalleryButton',
      label: 'Planimetrie',
      position: gtmPosition,
    }, {}, true));
      return;
    }
    sleep.apply(300).then(() => trackGTMEvents({
      category: 'Interaction',
      action: 'PushButton',
      label: 'ShowFloorplanSRP',
      position: gtmPosition,
    }, {}, true));
    slider.current.slickGoTo(media.floorplan);
  };

  const galCls = makeClassName([
    cls[mainCls],
    size,
    (maplist && !mapcard) && 'maplist',
    mapcard && 'mapcard',
  ]);

  const sliderCls = makeClassName([
    cls[`${mainCls}__slider`],
    topRound && 'topRound',
    botRound && 'botRound',
  ]);

  const linkCLick = (e) => actions.goToPDP({ e });
  const rightCLick = (e) => actions.goToPDP({ e, rightClick: true });

  const hasMediaTypes = media.hasVideo || media.has360 || media.has3dtour;
  const hasCounter =
    media.items.length > 1 ||
    (media.items.length === 1 &&
      media.items[0].uri !== '/mobileplaceholder.jpg');

  const imgFitContain = makeClassName([
    isFloorplan ? cls[`${mainCls}__slider__img-contain`] : '',
  ]);

  return (
    <GridItem className={galCls} behavior="fixed">
      <div ref={container} className={`${cls[`${mainCls}__wrap`]} is-rel`}>
        <Grid
          className={`${cls[`${mainCls}__cont`]} tp-s--s tp-w--s`}
          justify="center"
          align="center"
        >
          <div className={sliderCls}>
            {(visible || forceVisible) && (
              <ReactSlider
                ref={slider}
                arrows={false}
                infinite
                initialSlide={0}
                beforeChange={updateCurrent}
                draggable={false}
                swipe
              >
                {media.items.map((img, x) => (
                  <a
                    key={img.uri}
                    href={uri}
                    className={`${
                      cls[`${mainCls}__slider__imgcont`]
                    } tp-a--c c-bg--bg is-clickable`}
                    onClick={linkCLick}
                    onContextMenu={rightCLick}
                  >
                    {toLoad.includes(x) && (
                      <Image
                        src={img.uri}
                        alt={img.alt}
                        className={(media.floorplan && x >= media.floorplan) ? imgFitContain : ''}
                        isFloorplan={media.floorplan && x >= media.floorplan}
                      />
                    )}
                  </a>
                ))}
                <div className={cls[`${mainCls}__slider__imgcont`]}>
                  <CtaSlide
                    img={media.items[0]}
                    // goToFirst={goToFirst}
                    actions={actions}
                    isMobile={isMobile}
                  />
                </div>
              </ReactSlider>
            )}
            <button
              type="button"
              className={`${
                cls[`${mainCls}__slider__arrows`]
              } c-txt--secondary b--0 b-r--pill is-clickable`}
              onClick={prevSlide}
            >
              <Icon name={Arrow} width="13px" height="13px" />
            </button>
            <button
              type="button"
              className={`${
                cls[`${mainCls}__slider__arrows`]
              } right c-txt--secondary b--0 b-r--pill is-clickable`}
              onClick={nextSlide}
            >
              <Icon
                name={Arrow}
                width="13px"
                height="13px"
                className="rotate--180"
              />
            </button>
            {media.floorplan > 0 && (
              <Tooltip
                text="Vai alla planimetria"
                forceHidden={isFloorplan}
                topCoords={-41}
                className={`${cls[`${mainCls}__slider__floorplan`]}${
                  current === media.items.length ? ' hidden' : ''
                }`}
                classNameBaloon={maplist ? 'maplist' : null}
              >
                <button
                  type="button"
                  className={`${cls[`${mainCls}__slider__floorplan__btn`]}${
                    isFloorplan ? ' expanded' : ''
                  }${
                    isFloorplanClosing ? ' closing' : ''
                  } tp-w--m c-txt--f0 c-bg--w b--0 b-r--pill is-clickable is-rel`}
                  onClick={gotoFloorplan}
                >
                  {isFloorplan ? (
                    <div className={`${cls[`${mainCls}__slider__floorplan__btn__cnt-svg`]}
                      pl--s`}>
                      <Icon name={GoFullScreen} width="16px" height="16px" />
                    </div>
                  ) : (
                    <Icon name={FloorPlan} width="24px" height="24px" fill />
                  )}
                </button>
              </Tooltip>
            )}
            <Grid
              className={`${
                cls[`${mainCls}__slider__infos`]
              } tp-s--s tp-w--s c-txt--w${
                current === media.items.length ? ' hidden' : ''
              }`}
              align="center"
            >
              {hasMediaTypes && (
                <span className={`${cls[`${mainCls}__slider__medias`]} pr--xs`}>
                  {media.hasVideo && (
                    <span className="pl--xs">
                      <Icon name={Video} fill width="24px" height="24px" />
                    </span>
                  )}
                  {media.has360 && (
                    <span className="pl--xs">
                      <Icon
                        name={VirtualTour}
                        fill
                        width="24px"
                        height="24px"
                      />
                    </span>
                  )}
                  {media.has3dtour && (
                    <span className="pl--xs">
                      <Icon name={Matterpot} fill width="24px" height="24px" />
                    </span>
                  )}
                </span>
              )}
              {hasCounter && (
                <span
                  className={`${cls[`${mainCls}__slider__counter`]} tp-a--c`}
                >
                  {current + 1}/{media.items.length}
                </span>
              )}
            </Grid>
            {labels.length > 0 && (
              <Grid
                className={`${cls[`${mainCls}__slider__labels`]}${
                  current > 0 ? ' hidden' : ''
                }`}
              >
                {labels.map((label, x) => (
                  <GridItem
                    key={`${id}_${label}`}
                    className={`${cls[`${mainCls}__slider__label`]}${
                      x === 0 ? ' highlight c-txt--w' : ' c-bg--w c-txt--f5'
                    } ml--s pr--xs pl--xs tp-s--x2s tp-w--l tp--up`}
                  >
                    {label}
                  </GridItem>
                ))}
              </Grid>
            )}
          </div>
        </Grid>
      </div>
    </GridItem>
  );
}
